/**
 * User Detail Component
 * Displays detailed information about a user
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Card,
  CardContent,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import { PermissionGate } from '../Auth';
import { getApiErrorMessage } from '../../utils/errorMessages';
import { useErrorToast } from '../common';

const UserDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const api = useApi();
  const { showErrorToast } = useErrorToast();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (error) {
      showErrorToast(error);
      setError(null);
    }
  }, [error, showErrorToast]);

  useEffect(() => {
    loadUser();
  }, [id]);

  const loadUser = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/users/${id}`);

      if (response.data?.success || response.data) {
        setUser(response.data?.data || response.data);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load user'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/users/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/users');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={handleBack}>
          Back to Users
        </Button>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box>
        <Alert severity="warning">User not found</Alert>
        <Button startIcon={<BackIcon />} onClick={handleBack} sx={{ mt: 2 }}>
          Back to Users
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<BackIcon />} onClick={handleBack}>
            Back
          </Button>
          <Typography variant="h4" component="h1">
            User Details
          </Typography>
        </Box>
        <PermissionGate permission="manage_users">
          <Button variant="contained" startIcon={<EditIcon />} onClick={handleEdit}>
            Edit User
          </Button>
        </PermissionGate>
      </Box>

      <Grid container spacing={3}>
        {/* User Information */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              User Information
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" width="30%">
                      <Box display="flex" alignItems="center" gap={1}>
                        <BadgeIcon fontSize="small" color="action" />
                        Full Name
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" fontWeight={500}>
                        {user.FullName}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th">
                      <Box display="flex" alignItems="center" gap={1}>
                        <EmailIcon fontSize="small" color="action" />
                        Email
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">{user.Email}</Typography>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th">Status</TableCell>
                    <TableCell>
                      <Chip
                        label={user.IsActive ? 'Active' : 'Inactive'}
                        color={user.IsActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell component="th">
                      <Box display="flex" alignItems="center" gap={1}>
                        <CalendarIcon fontSize="small" color="action" />
                        Created Date
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">
                        {new Date(user.CreatedDate).toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>

                  {user.ModifiedDate && (
                    <TableRow>
                      <TableCell component="th">Last Modified</TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {new Date(user.ModifiedDate).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Roles & Tenants */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Roles & Access
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {user.roles && user.roles.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {user.roles.map((role, index) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <BusinessIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" color="text.secondary">
                          {role.TenantName}
                        </Typography>
                      </Box>
                      <Chip
                        label={role.RoleName}
                        color="primary"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No roles assigned
              </Typography>
            )}
          </Paper>

          {/* Quick Stats */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Stats
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Tenants
                </Typography>
                <Typography variant="h4">
                  {user.roles ? user.roles.length : 0}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Account Status
                </Typography>
                <Typography variant="h6" color={user.IsActive ? 'success.main' : 'text.secondary'}>
                  {user.IsActive ? 'Active' : 'Inactive'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserDetail;

